/* @flow */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import loadable from '@loadable/component';
import LazyLoad from 'react-lazyload';
import Breadcrumbs from '../../components/Breadcrumbs';
import ComplexCards from '../../components/ComplexCards';
import RatingMark from '../../components/RatingMark2.0';
import PopupQuestion from '../../components/PopupQuestion';
import { plural, getCity } from '../../utils';
import developerHeaderImage from '../../../public/images/developer-header.jpeg';
import { Container } from '../../components/Layout/styles';
import {
	Address,
	AddressIcon,
	AddressText,
	CardsSide,
	CardsWrapper,
	CitySelect,
	Complexes,
	ImportantKnowledge,
	ImportantKnowledgeBottom,
	ImportantKnowledgeBottomLeft,
	ImportantKnowledgeBottomRight,
	ImportantKnowledgeContent,
	ImportantKnowledgeText,
	ImportantKnowledgeTitle,
	Info,
	InfoItem,
	InfoItemValue,
	Main,
	MainText,
	MapWrapper,
	RatingTitle,
	Root,
	Title,
	HeadContainer,
	InfoTitle,
	InfoItemContainer,
	InfoItemTitle,
	ObjectTitle,
	DeveloperHeaderImage,
	RatingContainer,
	PaginationWrapper,
	DeveloperHead,
	RatingContainerBottom,
	ShortDescription,
	BreadcrumbsWrapper,
} from './styles';
import { getCityTitleByCityIds } from '../../utils/cities';
import type { TDeveloperData, TDeveloperComplex } from '../../types/dosierDetail';
import type { TCities, TCity } from '../../types/city';
import theme from '../../theme';
import { locationIcon } from '../../components/Icons';
import Paginate from '../../components/Paginate';
import { handleScrollTo } from '../../DOMUtils';
import Button from '../../components/Button2.0';
import { calcBounds, calcMinMax } from '../../utils/mapUtils';
import { SkeletonMap } from '../../components/Skeleton';

const DevelopersMap = loadable(() => import('../../components/DevelopersMap'), {
	ssr: true,
	fallback: <SkeletonMap />,
});

const DosierDetail = () => {
	const dosier: TDeveloperData = useSelector((state) => state.dosierDetail.dosierData);
	const city: TCity = useSelector((state) => state.city);
	const storeCities: TCities = useSelector((state) => state.cities);
	const isMobile: boolean = useSelector((state) => state.app.isMobile);

	const [popupInfo, setPopupInfo] = useState(false);
	const [problemVisible, setProblemVisible] = useState(false);
	const [cities, setCities] = useState([]);
	const [currentCity, setCurrentCity] = useState(null);
	const [filteredComplexes, setFilteredComplexes] = useState(dosier.complexes);
	const [currentPage, setCurrentPage] = useState(1);
	const [bounds, setBounds] = useState(null);

	const cityObject: TCity = currentCity && currentCity !== city.link ? getCity(currentCity, storeCities) : city;

	useEffect(() => {
		setCities(
			dosier?.complexes?.length > 0
				? [...new Set(dosier.complexes.map((item: TDeveloperComplex) => item?.city?.slug))]
				: [city?.link],
		);

		const isCityExist = getCity(currentCity, storeCities);
		if (!isCityExist) {
			setCities([city?.link]);
		}

		if (dosier?.isProblem) {
			setProblemVisible(true);
		}
	}, []);

	useEffect(() => {
		if (cities?.length > 0 && currentCity === null) {
			setCurrentCity(cities.includes(city?.link) ? city.link : cities[0]);
		}
	}, [cities]);

	useEffect(() => {
		if (dosier?.complexes?.length > 0 && cities?.length > 1) {
			setFilteredComplexes(
				dosier.complexes.filter((complex) =>
					getCity(currentCity, storeCities).cityIds.includes(complex.cityId),
				),
			);
		}
	}, [currentCity]);

	useEffect(() => {
		setFilteredComplexes(dosier.complexes);
	}, [dosier.complexes]);

	useEffect(() => {
		if (filteredComplexes?.length > 0) {
			setBounds(calcBounds(calcMinMax(filteredComplexes, cityObject.coords), 0));
		}
	}, [filteredComplexes]);

	const citySelectOptions =
		cities?.length > 0 &&
		cities.map((item) => ({
			value: getCity(item, storeCities).link,
			label: getCity(item, storeCities).title,
		}));

	const breadcrumbs = [
		{
			index: 1,
			title: 'Главная',
			link: '/',
		},
		{
			index: 2,
			title: 'Застройщики',
			link: `/${city.link}/zastroischiki/`,
		},
		{
			index: 3,
			title: dosier.name,
			link: `/zastroischiki/${dosier.slug}`,
		},
	];

	const getAverageRating = () => {
		let sum = 0;

		if (filteredComplexes?.length > 0) {
			filteredComplexes.forEach((complex) => {
				sum += complex.ratingComplex;
			});
			return Number((sum / filteredComplexes.length).toFixed(1));
		}

		return null;
	};

	const ratingGeneral = {
		id: 1,
		title: `средний рейтинг объектов застройщика в городе`,
		main: true,
		rating: getAverageRating(),
	};

	const handleChange = (option) => {
		if (currentCity !== option.value) {
			setCurrentCity(option.value);
		}
	};

	const handleInfoPopup = () => {
		setPopupInfo(!popupInfo);
	};

	const handleHideButtonClick = () => {
		setProblemVisible(false);
	};

	const renderDoiserAddresses = () => {
		const { addresses } = dosier;

		let cityValue = '';
		let cityAddress = '';

		if (addresses?.length > 0) {
			cityValue = getCityTitleByCityIds(addresses[0].cityId, storeCities);
			cityAddress = addresses[0].address;

			if (cityValue)
				return (
					<>
						<AddressIcon>{locationIcon}</AddressIcon>
						<AddressText>{`${cityValue}, ${cityAddress}`}</AddressText>
					</>
				);
		}

		return null;
	};

	const customStyles = {
		control: (provided) => ({
			...provided,
			width: isMobile ? '280px' : 440,
			border: '1px solid #F0F1F4',
			borderRadius: '4px',
		}),
		indicatorSeparator: () => ({
			display: 'none',
		}),
	};

	const onPageChange = (page) => async () => {
		setCurrentPage(page);
		handleScrollTo('cards', {
			delay: 0,
			smooth: true,
			offset: 0,
		});
	};

	const cardsOnPage = 9;

	const currentPageCards =
		filteredComplexes.length > cardsOnPage
			? filteredComplexes.slice((currentPage - 1) * cardsOnPage, currentPage * cardsOnPage)
			: filteredComplexes;

	const getValueText = (test, value) => {
		if (test) {
			return value;
		}
		return 'Нет данных';
	};

	const GENERAL_INFO = [
		{
			title: 'На рынке',
			value: getValueText(
				!!dosier?.workTime,
				`${dosier.workTime} ${plural(dosier.workTime, ['год', 'года', 'лет'])}`,
			),
		},
		{
			title: 'Строится',
			value: getValueText(
				!!dosier?.builtSquareMeters,
				dosier.builtSquareMeters > 1000
					? `${(dosier.builtSquareMeters / 1000).toFixed(0)} тыс. м²`
					: dosier.builtSquareMeters,
			),
		},
		{
			title: 'Введено',
			value: getValueText(
				!!dosier?.buildMeters,
				dosier.buildMeters > 1000 ? `${(dosier.buildMeters / 1000).toFixed(0)} тыс. м²` : dosier.buildMeters,
			),
		},
		{
			title: 'Строится ЖК',
			value: getValueText(!!dosier?.building, dosier.building),
		},
		{
			title: 'Сдано ЖК',
			value: getValueText(!!dosier?.built, dosier.built),
		},
		{
			title: 'Задержка сдачи',
			value: getValueText(
				!!dosier?.built,
				dosier.delayAvg
					? `${dosier.delayAvg} ${plural(dosier.delayAvg, ['месяц', 'месяца', 'месяцев'])}`
					: 'Без задержек',
			),
		},
	];

	return (
		<Root>
			<PopupQuestion visible={popupInfo} handleClosePopup={handleInfoPopup} question />
			{problemVisible && (
				<ImportantKnowledge>
					<ImportantKnowledgeContent>
						<ImportantKnowledgeTitle>Важно знать</ImportantKnowledgeTitle>
						<ImportantKnowledgeText>
							{dosier?.problemDescription
								? dosier.problemDescription
								: 'Застройщик банкрот или у него имеются проблемы'}
						</ImportantKnowledgeText>
						<Button
							color="white"
							size="s"
							style={{ padding: '0 20px', color: '#000' }}
							onClick={handleHideButtonClick}
						>
							Понял, спасибо
						</Button>
					</ImportantKnowledgeContent>
					<ImportantKnowledgeBottom>
						<ImportantKnowledgeBottomLeft />
						<ImportantKnowledgeBottomRight />
					</ImportantKnowledgeBottom>
				</ImportantKnowledge>
			)}
			<Container>
				<HeadContainer>
					<LazyLoad>
						<DeveloperHeaderImage image={developerHeaderImage} />
					</LazyLoad>
					<DeveloperHead>
						<BreadcrumbsWrapper>
							<Breadcrumbs data={breadcrumbs} color="#fff" colorLast={theme.toxicLimeColor} />
						</BreadcrumbsWrapper>
						<Title>{dosier.name}</Title>
						{dosier.ratingDeveloper && (
							<RatingContainer>
								<RatingMark value={dosier.ratingDeveloper} type="default" />
								<RatingTitle footer>средний рейтинг объектов застройщика</RatingTitle>
							</RatingContainer>
						)}
						{/* <ShortDescription></ShortDescription> */}
						<Address>{renderDoiserAddresses(cities, currentCity, storeCities, dosier)}</Address>
					</DeveloperHead>
				</HeadContainer>
				<Info>
					<InfoTitle>Общая информация</InfoTitle>
					<InfoItemContainer>
						{GENERAL_INFO.map((item) => (
							<InfoItem key={item.title}>
								<InfoItemTitle>{item.title}</InfoItemTitle>
								<InfoItemValue>{item.value}</InfoItemValue>
							</InfoItem>
						))}
					</InfoItemContainer>
				</Info>
				{dosier?.description && (
					<Main>
						<InfoTitle>О застройщике</InfoTitle>
						<MainText>{dosier.description}</MainText>
					</Main>
				)}
				{filteredComplexes?.length > 0 && (
					<CardsWrapper id="cards">
						<ObjectTitle>Объекты застройщика</ObjectTitle>
						<CardsSide>
							{cities?.length > 1 && citySelectOptions.length > 0 && (
								<CitySelect>
									<Select
										placeholder="Выберите регион"
										defaultValue={citySelectOptions.find((item) => item.value === currentCity)}
										value={citySelectOptions.find((item) => item.value === currentCity)}
										options={citySelectOptions}
										onChange={handleChange}
										isSearchable={false}
										styles={customStyles}
									/>
								</CitySelect>
							)}
							<RatingContainerBottom>
								<RatingMark value={ratingGeneral.rating} />
								<RatingTitle>{ratingGeneral.title}</RatingTitle>
							</RatingContainerBottom>
						</CardsSide>
						<Complexes>
							<ComplexCards complexes={currentPageCards} />
						</Complexes>
					</CardsWrapper>
				)}
				{filteredComplexes?.length > 0 && (
					<>
						<PaginationWrapper>
							{filteredComplexes?.length > cardsOnPage && (
								<Paginate
									currentPage={currentPage}
									setCurrentPage={(page) => onPageChange(page)}
									totalPosts={filteredComplexes.length}
									postPerPage={cardsOnPage}
								/>
							)}
						</PaginationWrapper>
						<ObjectTitle>Расположение на карте</ObjectTitle>
						<MapWrapper id="map">
							<DevelopersMap bounds={bounds} city={cityObject} complexes={filteredComplexes} />
						</MapWrapper>
					</>
				)}
			</Container>
		</Root>
	);
};

export default DosierDetail;
